"use client";

import Image from "next/image";
import Link from "next/link";

import { getCoursesMarketplaceCopy } from "@/content/courses-marketplace-copy";
import type { Locale } from "@/i18n-config";
import { toSanityOriginalImageUrl } from "@/lib/sanity/image";
import { appendVideoStartFragment, resolveCoursePreviewVideo } from "@/lib/courses/video-preview";
import type { Course } from "@/types";
import { cn } from "@/utils/cn";

function inferDuration(course: Course): number {
  if (
    typeof course.durationMinutes === "number" &&
    Number.isFinite(course.durationMinutes) &&
    course.durationMinutes > 0
  ) {
    return Math.round(course.durationMinutes);
  }

  const lessonCount = course.lessons?.length ?? 0;
  if (lessonCount > 0) {
    return Math.max(20, lessonCount * 20);
  }

  return 45;
}

function inferDifficulty(course: Course, durationMinutes: number): "beginner" | "standard" | "advanced" {
  const normalizedDifficulty = (course.difficulty ?? "").trim().toLowerCase();
  if (normalizedDifficulty === "beginner") return "beginner";
  if (normalizedDifficulty === "standard" || normalizedDifficulty === "intermediate") return "standard";
  if (normalizedDifficulty === "advanced") return "advanced";
  if (durationMinutes >= 90) return "advanced";
  if (durationMinutes >= 45) return "standard";
  return "beginner";
}

function inferCategory(course: Course): "coding" | "practice" | "mindset" {
  const source = `${course.category ?? ""} ${course.section ?? ""} ${course.title ?? ""} ${course.description ?? ""}`.toLowerCase();

  if (/(mindset|mind|clarity|focus|decision|overwhelm|confidence|motivation|мыш|мисл|підхід|подход)/.test(source)) {
    return "mindset";
  }

  if (/(practice|project|build|launch|workflow|execute|ship|apply|практик|реал|запуск|кейс)/.test(source)) {
    return "practice";
  }

  return "coding";
}

function toSingleLine(text: string, maxLength = 110): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}...`;
}

function buildOutcomeLine(course: Course, fallback: string): string {
  const preferred = course.shortOutcomeLine ?? course.description ?? "";
  const normalized = toSingleLine(preferred, 108);
  if (normalized.length > 0) return normalized;
  return fallback;
}

function resolveCourseCover(course: Course) {
  const altFallback = `${course.title} cover`;
  const previewVideo = resolveCoursePreviewVideo(course.coursePreviewVideo);

  if (course.courseCoverImage?.source === "url" && course.courseCoverImage.externalUrl) {
    return {
      src: course.courseCoverImage.externalUrl,
      alt: toSingleLine(course.courseCoverImage.alt ?? altFallback, 140) || altFallback,
      isExternal: true,
      videoSrc: null,
      videoThumbnailSrc: null,
      hasEmbeddedVideo: false,
    };
  }

  const uploadedCover = toSanityOriginalImageUrl(course.courseCoverImage?.image);
  if (uploadedCover) {
    return {
      src: uploadedCover,
      alt: toSingleLine(course.courseCoverImage?.alt ?? altFallback, 140) || altFallback,
      isExternal: false,
      videoSrc: null,
      videoThumbnailSrc: null,
      hasEmbeddedVideo: false,
    };
  }

  return {
    src: null,
    alt: altFallback,
    isExternal: false,
    videoSrc: previewVideo?.src ?? null,
    videoThumbnailSrc: previewVideo?.thumbnailSrc ?? null,
    hasEmbeddedVideo: Boolean(previewVideo?.embedUrl),
  };
}

export function CourseCard({
  course,
  lang = "en",
  variant = "default",
  className,
}: {
  course: Course;
  lang?: Locale;
  variant?: "default" | "immersive";
  className?: string;
}) {
  const copy = getCoursesMarketplaceCopy(lang);
  const durationMinutes = inferDuration(course);
  const difficulty = inferDifficulty(course, durationMinutes);
  const difficultyLabel = copy.difficultyOptions[difficulty];
  const accessBadgeLabel = course.isFree ? copy.accessOptions.free : copy.accessOptions.paid;
  const categoryLabel = course.category?.trim() || copy.categories[inferCategory(course)];
  const outcomeLine = buildOutcomeLine(course, copy.problemFallback);
  const cover = resolveCourseCover(course);
  const isImmersive = variant === "immersive";

  return (
    <article
      className={cn(
        isImmersive
          ? "flex h-full min-h-96 flex-col overflow-hidden rounded-[28px] border border-[#d8cab2]/16 bg-[linear-gradient(180deg,rgba(27,36,33,0.95),rgba(15,21,19,0.90))] shadow-[0_24px_56px_-34px_rgba(0,0,0,0.72)] transition-colors hover:border-[#0f766e]/32 sm:min-h-0"
          : "flex h-full min-h-96 flex-col overflow-hidden rounded-xl border border-[#d8cab2] bg-[#fffaf1] shadow-[0_6px_14px_rgba(34,47,42,0.06)] transition-colors hover:border-[#baa37f] sm:min-h-0 sm:rounded-2xl",
        className
      )}
    >
      <div
        className={cn(
          "flex items-start justify-between gap-3 px-3 py-2.5 sm:px-4 sm:py-3",
          isImmersive ? "border-b border-[#d8cab2]/12 bg-white/[0.04]" : "border-b border-[#e3d7c4] bg-[#fff9ef]"
        )}
      >
        <div className="min-w-0">
          <p className={cn("text-[11px] font-semibold leading-tight sm:text-xs", isImmersive ? "text-[#f6f1e7]" : "text-[#2f433d]")}>
            {durationMinutes} {copy.durationUnit}
          </p>
          <p className={cn("mt-1 text-[11px] leading-tight sm:text-xs", isImmersive ? "text-white/58" : "text-[#5a6d66]")}>
            {difficultyLabel}
          </p>
        </div>
        <span
          className={cn(
            "inline-flex items-center rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.06em] sm:text-[11px]",
            isImmersive
              ? "border border-[#d8cab2]/18 bg-white/[0.06] text-[#f6f1e7]"
              : "border border-[#d1c4ad] bg-white text-[#42564f]"
          )}
        >
          {accessBadgeLabel}
        </span>
      </div>

      <div
        className={cn(
          "relative h-32 w-full overflow-hidden sm:h-auto sm:aspect-video",
          isImmersive ? "border-b border-[#d8cab2]/12 bg-[#1d2622]" : "border-b border-[#dccfb9] bg-[#efe4d2]"
        )}
      >
        {cover.src ? (
          cover.isExternal ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={cover.src} alt={cover.alt} className="h-full w-full object-cover" loading="lazy" decoding="async" />
          ) : (
            <Image
              src={cover.src}
              alt={cover.alt}
              fill
              unoptimized
              sizes="(min-width: 1280px) 31vw, (min-width: 768px) 48vw, 82vw"
              className="object-cover"
              loading="lazy"
            />
          )
        ) : cover.videoThumbnailSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cover.videoThumbnailSrc}
            alt={cover.alt}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : cover.videoSrc && !cover.hasEmbeddedVideo ? (
          <video className="h-full w-full object-cover" muted playsInline preload="metadata" src={appendVideoStartFragment(cover.videoSrc)} />
        ) : (
          <div
            className={cn(
              "flex h-full w-full items-center justify-center text-xs font-semibold tracking-[0.08em]",
              isImmersive ? "bg-[#1d2622] text-[#d8cab2]/68" : "bg-[#efe4d2] text-[#7d6f59]"
            )}
          >
            {copy.noPreview}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-3.5 sm:p-4">
        <h3
          className={cn(
            "line-clamp-2 h-[2.6rem] overflow-hidden text-ellipsis text-sm font-semibold leading-snug [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] sm:h-auto sm:min-h-[2.8rem] sm:text-base",
            isImmersive ? "text-[#fffaf1]" : "text-[#1f2a27]"
          )}
        >
          {course.title}
        </h3>

        <p
          className={cn(
            "mt-2 line-clamp-2 h-[2.9rem] overflow-hidden text-ellipsis text-xs leading-relaxed [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] sm:h-auto sm:min-h-[2.8rem] sm:text-sm",
            isImmersive ? "text-white/64" : "text-[#4d6059]"
          )}
        >
          {outcomeLine}
        </p>

        <p className={cn("mt-2.5 min-h-[1.1rem] truncate text-[11px] sm:mt-4 sm:min-h-0 sm:text-xs", isImmersive ? "text-white/46" : "text-[#5a6d66]")}>
          {categoryLabel}
        </p>

        <Link
          href={`/${lang}/courses/${course.slug.current}`}
          prefetch={false}
          className={cn(
            "mt-auto inline-flex min-h-9 w-full items-center justify-center px-3 text-xs font-semibold outline-none transition-colors sm:mt-4 sm:min-h-11 sm:px-4 sm:text-sm",
            isImmersive
              ? "rounded-xl border border-[#d8cab2]/18 bg-white/[0.04] text-[#fffaf1] hover:border-[#0f766e]/30 hover:bg-white/[0.08] focus-visible:border-[#0f766e] focus-visible:ring-2 focus-visible:ring-[#0f766e]/25"
              : "rounded-lg border border-[#cbbca2] bg-white text-[#29453f] hover:border-[#aa9471] hover:bg-[#fbf6eb] focus-visible:border-[#0f766e] focus-visible:ring-2 focus-visible:ring-[#0f766e]/25 sm:rounded-xl"
          )}
        >
          {copy.viewDetails}
        </Link>
      </div>
    </article>
  );
}
